/**
 * GoalFormDialog — create / edit a goal (Story 18.3 contract; Story 18.8
 * cross-client + Budget extension per AC#8).
 *
 * NOTE (Story 18.8 sequencing): 18.3 had NOT landed when 18.8 was
 * implemented (ready-for-dev, no component on disk), so this dialog was
 * created HERE under the tolerate-if-missing rule (same as goal-progress.ts).
 * 18.3 OWNS this component; 18.8 adds two ADDITIVE branches:
 *   1. Cross-client mode — when `clientId` is ABSENT, a required Client
 *      selector renders as the FIRST field (the `clients` prop is supplied by
 *      the Goals page). Metric-mode platform/metric selects re-key to the
 *      SELECTED client (useClientIntegrations(selectedClientId)); changing the
 *      client clears platform+metric in ONE state patch (the single-patch
 *      lesson). The page POSTs to POST /api/clients/:selectedClientId/goals.
 *   2. A third goal-type option: Budget (the cross-client surface offers
 *      Budget / Metric / Custom). Budget forces unit=currency (selector
 *      hidden), shows an optional ad-platform select, and inverted-pace helper
 *      copy. When `clientId` IS provided (the client-page surface), the dialog
 *      keeps Metric / Custom only.
 *
 * §4.2 centered modal Dialog (max-w-md, always-mounted §4.6, reset-on-OPEN,
 * close blocked while pending, §6 stagger). NEVER a Sheet (§4.1).
 *
 * Field grammar = the 5.30 goalWidgetConfigSchema (target value, unit, period
 * type, thresholds 80/100, custom-period date pair). Saving POSTs (create) or
 * PATCHes (edit) 18.3's per-client endpoint.
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button, Input, Label } from "@provenleap/ui";
import { motion, AnimatePresence } from "motion/react";
import { Info } from "@phosphor-icons/react";
import { PortalLightTooltip } from "../reports/PortalLightTooltip";
import {
  PLAN_LIMITS,
  SAFETY_CAPS,
  getMinimumPlanForFeature,
  type Plan,
} from "@provenleap/types";
import { apiPost, apiPatch } from "../../lib/apiClient";
import { useClientIntegrations } from "../../hooks/useClientIntegrations";
import { useAvailableMetrics } from "../../hooks/useAvailableMetrics";
import { usePlanLimits } from "../../hooks/usePlanLimits";
import { GOALS_QUERY_KEY } from "../../hooks/useGoals";
import { UpgradeLimitModal } from "../billing/UpgradeLimitModal";
import { DialogSelect } from "./DialogSelect";
import { PICKER_PLATFORMS, logoUrl } from "../../lib/integrations";
import { countGoalsForGate, type ClientGateInput } from "../../lib/goalPlanGate";
import {
  GOAL_PERIOD_TYPES,
  type GoalType,
  type GoalPeriodType,
  type GoalUnitType,
  type GoalListItem,
} from "../../lib/goalPageDisplay";

/**
 * Story 18.10 — per-client goal gating at dialog submit. The modal state the
 * dialog raises when the selected client is at/over the plan's per-client
 * goalLimit (paid tiers) or when the server returns a 403 UpgradeRequiredError
 * (defense-in-depth against a stale loaded list).
 */
interface GoalGateModalState {
  currentPlan: Plan;
  currentUsage: number;
  currentLimit: number;
  upgradePlan?: Plan;
  nextTierLimit: number | null;
}

/** Shape of the RFC 9457 UpgradeRequiredError body (snake_case extensions). */
interface UpgradeProblem {
  type?: string;
  current_plan?: Plan;
  current_limit?: number;
  current_usage?: number;
  upgrade_to?: Plan;
  next_tier_limit?: number | null;
}

function isUpgradeProblem(err: unknown): err is { status: number } & UpgradeProblem {
  return (
    typeof err === "object" &&
    err !== null &&
    (err as { status?: number }).status === 403 &&
    (err as { type?: string }).type ===
      "https://provenleap.com/errors/upgrade-required"
  );
}

/**
 * Builds the UpgradeLimitModal state for a per-client block (AC#8). Returns
 * null when `count` is below the plan's per-client limit (no block). Mirrors
 * the server's feature-guard math: count < limit allowed; null limit allowed
 * up to the safety cap; upgrade target = getMinimumPlanForFeature(count+1).
 */
function buildPerClientGate(plan: Plan, count: number): GoalGateModalState | null {
  const limit = PLAN_LIMITS[plan].goalLimit;

  if (limit === null) {
    const cap = SAFETY_CAPS.goalLimit;
    if (cap !== undefined && count >= cap) {
      return {
        currentPlan: plan,
        currentUsage: count,
        currentLimit: cap,
        upgradePlan: undefined, // top tier — no sellable upgrade
        nextTierLimit: null,
      };
    }
    return null; // unlimited, under cap
  }

  if (count < limit) return null; // allowed

  const upgrade = getMinimumPlanForFeature("goalLimit", count + 1);
  const isMax = upgrade === plan;
  const nextLimit = isMax ? null : PLAN_LIMITS[upgrade].goalLimit;
  return {
    currentPlan: plan,
    currentUsage: count,
    currentLimit: limit,
    upgradePlan: isMax ? undefined : upgrade,
    nextTierLimit: typeof nextLimit === "number" ? nextLimit : null,
  };
}

/** Maps an RFC 9457 UpgradeRequiredError body to the modal state (AC#9). */
function gateFromProblem(p: UpgradeProblem): GoalGateModalState {
  return {
    currentPlan: p.current_plan ?? "free",
    currentUsage: p.current_usage ?? 0,
    currentLimit: p.current_limit ?? 0,
    upgradePlan: p.upgrade_to,
    nextTierLimit: p.next_tier_limit ?? null,
  };
}

/**
 * Inline field label + a supplementary Info icon that reveals a short
 * plain-language explanation on hover (reuses the report PortalLightTooltip
 * bubble as-is). The real <Label htmlFor> stays intact for accessibility; the
 * Info icon is decorative-but-described (aria-label "What is this?"), so screen
 * readers still announce the explanation without it stealing the field's name.
 * Copy is refined against docs/user-guide/goals.md, one short sentence each.
 */
function FieldLabel({
  htmlFor,
  children,
  hint,
}: {
  htmlFor: string;
  children: React.ReactNode;
  hint: string;
}) {
  return (
    <span className="flex items-center gap-1.5">
      <Label htmlFor={htmlFor}>{children}</Label>
      <PortalLightTooltip label={hint}>
        <span
          aria-label="What is this?"
          className="inline-flex items-center cursor-help text-muted-foreground"
        >
          <Info size={14} aria-hidden="true" className="shrink-0" />
        </span>
      </PortalLightTooltip>
    </span>
  );
}

export interface GoalFormClientOption {
  id: string;
  name: string;
}

interface GoalFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When provided, locks the dialog to a single client (client-page surface,
   *  Metric/Custom only). When ABSENT, renders the cross-client Client
   *  selector + the Budget type option (Goals-page surface). */
  clientId?: string;
  /** Active clients for the cross-client selector (cross-client mode only). */
  clients?: GoalFormClientOption[];
  /** When set, the dialog is in EDIT mode and pre-fills from this goal. */
  goal?: GoalListItem | null;
  onSaved?: () => void;
  /**
   * Story 18.10 — the loaded goals + clients (with demoMode) the per-client
   * submit gate counts against (AC#8). When BOTH are supplied (the 18-8 Goals
   * page), CREATE submits on a paid tier run the per-client check before
   * firing the mutation; at/over the limit the dialog raises an
   * UpgradeLimitModal instead. Omitted on the 18-3 client-page surface — that
   * surface keeps 18-3's behavior (the 403 fallback below still applies
   * everywhere, defense-in-depth). EDIT never gates (creation-only policy).
   */
  goalsForGate?: readonly { clientId: string }[];
  clientsForGate?: readonly ClientGateInput[];
}

interface FormState {
  clientId: string;
  name: string;
  goalType: GoalType;
  metricKey: string;
  platform: string;
  targetValue: string;
  /** Optional starting value — seeds truthful progress instantly (AC#7). */
  currentValue: string;
  unit: GoalUnitType;
  periodType: GoalPeriodType;
  periodStart: string; // yyyy-mm-dd
  periodEnd: string;
  thresholdWarning: string;
  thresholdDanger: string;
}

function emptyState(clientId?: string): FormState {
  return {
    clientId: clientId ?? "",
    name: "",
    goalType: "metric",
    metricKey: "",
    platform: "",
    targetValue: "",
    currentValue: "",
    unit: "number",
    periodType: "monthly",
    periodStart: "",
    periodEnd: "",
    thresholdWarning: "80",
    thresholdDanger: "100",
  };
}

function toDateInput(ms: number | null): string {
  if (ms === null) return "";
  return new Date(ms).toISOString().slice(0, 10);
}

function stateFromGoal(goal: GoalListItem): FormState {
  return {
    clientId: goal.clientId,
    name: goal.name,
    goalType: goal.goalType,
    metricKey: goal.metricKey ?? "",
    platform: goal.platform ?? "",
    targetValue: String(goal.targetValue),
    currentValue:
      goal.currentValue != null && goal.currentValue !== 0
        ? String(goal.currentValue)
        : "",
    unit: goal.unit,
    periodType: goal.periodType,
    periodStart: toDateInput(goal.periodStart),
    periodEnd: toDateInput(goal.periodEnd),
    thresholdWarning: String(goal.thresholdWarning),
    thresholdDanger: String(goal.thresholdDanger),
  };
}

export function GoalFormDialog({
  open,
  onOpenChange,
  clientId,
  clients,
  goal,
  onSaved,
  goalsForGate,
  clientsForGate,
}: GoalFormDialogProps) {
  const queryClient = useQueryClient();
  const { plan } = usePlanLimits();
  const isEdit = !!goal;
  const isCrossClient = clientId === undefined;
  const [form, setForm] = useState<FormState>(() => emptyState(clientId));
  const isBudget = form.goalType === "budget";
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Story 18.10 — non-null while the per-client / 403 upgrade modal is shown.
  const [gateModal, setGateModal] = useState<GoalGateModalState | null>(null);

  // Reset on OPEN (never on close — §4.6).
  useEffect(() => {
    if (!open) return;
    setForm(goal ? stateFromGoal(goal) : emptyState(clientId));
    setErrors({});
  }, [open, goal, clientId]);

  // Metric-mode selectors re-key to the selected client.
  const effectiveClientId = clientId ?? (form.clientId || undefined);
  const { integrations } = useClientIntegrations(effectiveClientId);
  const { allMetrics } = useAvailableMetrics(effectiveClientId);

  const platformOptions = useMemo(
    () => Array.from(new Set(integrations.map((i) => i.type))),
    [integrations],
  );
  const metricOptions = useMemo(() => {
    const list = form.platform
      ? allMetrics.filter((m) => m.platform === form.platform)
      : allMetrics;
    return list;
  }, [allMetrics, form.platform]);

  // ── DialogSelect option arrays (replace the former native <select>s) ──
  const clientSelectOptions = useMemo(
    () => (clients ?? []).map((cl) => ({ value: cl.id, label: cl.name })),
    [clients],
  );
  // Goal type: the cross-client surface offers Budget; the locked surface
  // keeps Metric / Custom only (18.8 additive branch).
  const goalTypeOptions = useMemo(
    () => [
      ...(isCrossClient ? [{ value: "budget", label: "Budget" }] : []),
      { value: "metric", label: "Metric Goal" },
      { value: "custom", label: "Custom" },
    ],
    [isCrossClient],
  );
  const platformSelectOptions = useMemo(() => {
    // The empty/placeholder row is context-aware: platform selection depends on
    // a client (its connected integrations are the source), so we gate on it.
    const emptyLabel = !effectiveClientId
      ? "Select a client first…"
      : platformOptions.length === 0
        ? "No connected platforms for this client"
        : isBudget
          ? "All platforms (total)"
          : "Select a platform…";
    return [
      { value: "", label: emptyLabel },
      // Carry each platform's logo (from PICKER_PLATFORMS via logoUrl) so the
      // dropdown shows the icon next to the key, matching the goals table.
      ...platformOptions.map((p) => {
        const meta = PICKER_PLATFORMS.find((x) => x.platform === p);
        return {
          value: p,
          label: p,
          iconUrl: meta?.domain ? logoUrl(meta.domain) : undefined,
        };
      }),
    ];
  }, [platformOptions, isBudget, effectiveClientId]);
  const metricSelectOptions = useMemo(
    () =>
      metricOptions.map((m) => ({
        value: m.key,
        label: m.label ?? m.key,
      })),
    [metricOptions],
  );
  const unitOptions = useMemo(
    () => [
      { value: "number", label: "Number" },
      { value: "currency", label: "Currency" },
      { value: "percentage", label: "Percentage" },
    ],
    [],
  );
  const periodOptions = useMemo(
    () =>
      GOAL_PERIOD_TYPES.map((p) => ({
        value: p,
        label: p.charAt(0).toUpperCase() + p.slice(1),
      })),
    [],
  );

  function patch(next: Partial<FormState>) {
    setForm((prev) => ({ ...prev, ...next }));
  }

  // Changing the client clears platform + metric in ONE patch.
  function handleClientChange(nextClientId: string) {
    patch({ clientId: nextClientId, platform: "", metricKey: "" });
  }

  function handleGoalTypeChange(nextType: GoalType) {
    if (nextType === "budget") {
      // Budget forces currency + drops the metric key.
      patch({ goalType: nextType, unit: "currency", metricKey: "" });
    } else {
      patch({ goalType: nextType });
    }
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const targetClientId = clientId ?? form.clientId;
      const payload: Record<string, unknown> = {
        name: form.name.trim(),
        goalType: form.goalType,
        targetValue: Number(form.targetValue),
        unit: form.goalType === "budget" ? "currency" : form.unit,
        periodType: form.periodType,
        thresholdWarning: Number(form.thresholdWarning),
        thresholdDanger: Number(form.thresholdDanger),
      };
      if (form.goalType === "metric") payload.metricKey = form.metricKey;
      if (form.platform) payload.platform = form.platform;
      // Optional starting baseline (both modes) — seeds demo progress instantly.
      if (form.currentValue.trim() !== "") {
        payload.currentValue = Number(form.currentValue);
      }
      if (form.periodType === "custom") {
        payload.periodStart = new Date(form.periodStart).getTime();
        payload.periodEnd = new Date(form.periodEnd).getTime();
      }

      const res = isEdit
        ? await apiPatch(
            `/api/clients/${targetClientId}/goals/${goal!.id}`,
            payload,
          )
        : await apiPost(`/api/clients/${targetClientId}/goals`, payload);
      const json = await res
        .json()
        .catch(() => ({ detail: "Server error. Please try again." }));
      if (!res.ok) throw { status: res.status, ...json };
      return { json, targetClientId };
    },
    onSuccess: ({ targetClientId }) => {
      queryClient.invalidateQueries({ queryKey: GOALS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ["client-goals", targetClientId] });
      onOpenChange(false);
      onSaved?.();
    },
    // AC#9 — server-403 fallback (defense in depth; the loaded list can be
    // stale, e.g. a goal created in another tab). A 403 UpgradeRequiredError
    // opens the UpgradeLimitModal from the response extensions — never a raw
    // toast, never a swallowed error. All other errors fall through to the
    // inline error (18-3's existing handling via mutation.isError below).
    onError: (err) => {
      if (isUpgradeProblem(err)) {
        setGateModal(gateFromProblem(err));
      }
    },
  });

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (isCrossClient && !form.clientId) e.clientId = "Select a client";
    if (!form.name.trim()) e.name = "Name is required";
    const target = Number(form.targetValue);
    if (!form.targetValue || Number.isNaN(target) || target <= 0)
      e.targetValue = "Enter a positive target value";
    if (form.goalType === "metric" && !form.metricKey)
      e.metricKey = "Select a metric";
    if (form.currentValue.trim() !== "") {
      const cur = Number(form.currentValue);
      if (Number.isNaN(cur) || cur < 0)
        e.currentValue = "Enter a value of 0 or more";
    }
    if (form.periodType === "custom") {
      if (!form.periodStart || !form.periodEnd) {
        e.periodStart = "Start and end dates are required";
      } else if (
        new Date(form.periodEnd).getTime() <= new Date(form.periodStart).getTime()
      ) {
        e.periodStart = "End date must be after the start date";
      }
    }
    const warn = Number(form.thresholdWarning);
    const danger = Number(form.thresholdDanger);
    if (warn > danger) e.thresholdWarning = "Warning must be ≤ danger";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    if (!validate()) return;

    // AC#8 — per-client submit gate (paid tiers, CREATE only). EDIT never
    // gates (creation-only downgrade policy). Runs only when the page supplied
    // the goals+clients gate inputs; the 18-3 client-page surface omits them
    // and relies on the server 403 fallback (AC#9) instead.
    if (!isEdit && goalsForGate && clientsForGate && plan !== "free") {
      const targetClientId = clientId ?? form.clientId;
      if (targetClientId) {
        const count = countGoalsForGate(
          goalsForGate,
          clientsForGate,
          plan,
          targetClientId,
        );
        if (count !== undefined) {
          const gate = buildPerClientGate(plan, count);
          if (gate) {
            setGateModal(gate);
            return; // block the mutation
          }
        }
      }
    }

    mutation.mutate();
  }

  function handleOpenChange(next: boolean) {
    if (!next && mutation.isPending) return; // close blocked while pending
    onOpenChange(next);
  }

  let fieldIndex = 0;
  const nextDelay = () => 0.05 * (++fieldIndex);

  return (
    <>
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>

            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg max-h-[85vh] overflow-y-auto outline-none"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                <div className="flex flex-col space-y-1.5 text-left">
                  <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                    {isEdit ? "Edit Goal" : "Create Goal"}
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description className="text-sm text-muted-foreground">
                    {isEdit
                      ? "Update this goal's target, period, and thresholds."
                      : "Set a target to track progress across the period."}
                  </DialogPrimitive.Description>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4 mt-6">
                  {/* Cross-client: required Client selector FIRST */}
                  {isCrossClient && (
                    <motion.div
                      className="space-y-2"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: nextDelay() }}
                    >
                      <FieldLabel
                        htmlFor="goal-client"
                        hint="The client this goal belongs to."
                      >
                        Client
                      </FieldLabel>
                      <DialogSelect
                        id="goal-client"
                        ariaLabel="Client"
                        value={form.clientId}
                        disabled={isEdit}
                        onChange={handleClientChange}
                        options={clientSelectOptions}
                        placeholder="Select a client…"
                        searchPlaceholder="Search clients…"
                      />
                      {errors.clientId && (
                        <p className="text-[12px] text-destructive">{errors.clientId}</p>
                      )}
                    </motion.div>
                  )}

                  {/* Goal type */}
                  <motion.div
                    className="space-y-2"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: nextDelay() }}
                  >
                    <FieldLabel
                      htmlFor="goal-type"
                      hint="What the goal tracks: a metric growing up to a target, a budget staying under a spending cap, or a custom number you maintain by hand."
                    >
                      Goal type
                    </FieldLabel>
                    <DialogSelect
                      id="goal-type"
                      ariaLabel="Goal type"
                      value={form.goalType}
                      onChange={(v) => handleGoalTypeChange(v as GoalType)}
                      options={goalTypeOptions}
                    />
                  </motion.div>

                  {/* Name */}
                  <motion.div
                    className="space-y-2"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: nextDelay() }}
                  >
                    <FieldLabel
                      htmlFor="goal-name"
                      hint="A short label for this goal, shown in your goals list and in reports."
                    >
                      Name
                    </FieldLabel>
                    <Input
                      id="goal-name"
                      className="rounded-lg"
                      value={form.name}
                      onChange={(e) => patch({ name: e.target.value })}
                      placeholder="e.g. Increase conversions"
                    />
                    {errors.name && (
                      <p className="text-[12px] text-destructive">{errors.name}</p>
                    )}
                  </motion.div>

                  {/* Platform (metric: ad/data platform; budget: optional ad platform) */}
                  {(form.goalType === "metric" || isBudget) && (
                    <motion.div
                      className="space-y-2"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: nextDelay() }}
                    >
                      <FieldLabel
                        htmlFor="goal-platform"
                        hint={
                          isBudget
                            ? "Which connected account to cap — leave empty to limit total spend across all platforms."
                            : "The connected account the tracked metric comes from (e.g. Google Analytics, Google Ads)."
                        }
                      >
                        Platform{" "}
                        {isBudget && (
                          <span className="text-[11px] text-muted-foreground">
                            (optional — empty = cross-platform total)
                          </span>
                        )}
                      </FieldLabel>
                      <DialogSelect
                        id="goal-platform"
                        ariaLabel="Platform"
                        value={form.platform}
                        onChange={(v) => patch({ platform: v, metricKey: "" })}
                        options={platformSelectOptions}
                        disabled={!effectiveClientId}
                        placeholder={
                          isBudget ? "All platforms (total)" : "Select a platform…"
                        }
                        searchPlaceholder="Search platforms…"
                      />
                    </motion.div>
                  )}

                  {/* Metric key (metric goals only) */}
                  {form.goalType === "metric" && (
                    <motion.div
                      className="space-y-2"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: nextDelay() }}
                    >
                      <FieldLabel
                        htmlFor="goal-metric"
                        hint="The specific number this goal tracks toward the target (e.g. sessions, conversions, spend)."
                      >
                        Metric
                      </FieldLabel>
                      <DialogSelect
                        id="goal-metric"
                        ariaLabel="Metric"
                        value={form.metricKey}
                        onChange={(v) => patch({ metricKey: v })}
                        options={metricSelectOptions}
                        placeholder="Select a metric…"
                        searchPlaceholder="Search metrics…"
                        searchable
                      />
                      {errors.metricKey && (
                        <p className="text-[12px] text-destructive">{errors.metricKey}</p>
                      )}
                    </motion.div>
                  )}

                  {/* Target value + unit */}
                  <motion.div
                    className="grid grid-cols-2 gap-3"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: nextDelay() }}
                  >
                    <div className="space-y-2">
                      <FieldLabel
                        htmlFor="goal-target"
                        hint="The number you are aiming for — a finish line to reach for a metric goal, or a limit to stay under for a budget goal."
                      >
                        Target value
                      </FieldLabel>
                      <Input
                        id="goal-target"
                        type="number"
                        className="rounded-lg"
                        value={form.targetValue}
                        onChange={(e) => patch({ targetValue: e.target.value })}
                        placeholder="0"
                      />
                      {errors.targetValue && (
                        <p className="text-[12px] text-destructive">{errors.targetValue}</p>
                      )}
                    </div>
                    {/* Budget forces currency — hide the unit selector. */}
                    {!isBudget && (
                      <div className="space-y-2">
                        <FieldLabel
                          htmlFor="goal-unit"
                          hint="How the value is shown: a plain number, a currency amount, or a percentage."
                        >
                          Unit
                        </FieldLabel>
                        <DialogSelect
                          id="goal-unit"
                          ariaLabel="Unit"
                          value={form.unit}
                          onChange={(v) => patch({ unit: v as GoalUnitType })}
                          options={unitOptions}
                        />
                      </div>
                    )}
                  </motion.div>

                  {/* Optional starting current value (both modes) — seeds
                      truthful progress instantly, before any cron exists. The
                      label leans on "Current value" for custom goals (manual
                      tracking) and "Starting value" otherwise. */}
                  <motion.div
                    className="space-y-2"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: nextDelay() }}
                  >
                    <FieldLabel
                      htmlFor="goal-current"
                      hint={
                        form.goalType === "custom"
                          ? "The latest value you have reached so far — you keep this up to date by hand for custom goals."
                          : "Progress you already have when creating the goal, so it starts from the truth instead of zero."
                      }
                    >
                      {form.goalType === "custom" ? "Current value" : "Starting value"}{" "}
                      <span className="text-[11px] text-muted-foreground">
                        (optional)
                      </span>
                    </FieldLabel>
                    <Input
                      id="goal-current"
                      type="number"
                      className="rounded-lg"
                      value={form.currentValue}
                      onChange={(e) => patch({ currentValue: e.target.value })}
                      placeholder="0"
                    />
                    {errors.currentValue && (
                      <p className="text-[12px] text-destructive">
                        {errors.currentValue}
                      </p>
                    )}
                  </motion.div>

                  {/* Period type */}
                  <motion.div
                    className="space-y-2"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: nextDelay() }}
                  >
                    <FieldLabel
                      htmlFor="goal-period"
                      hint="The time window the goal measures and resets over (monthly, quarterly, yearly, or a custom date range)."
                    >
                      Period
                    </FieldLabel>
                    <DialogSelect
                      id="goal-period"
                      ariaLabel="Period"
                      value={form.periodType}
                      onChange={(v) => patch({ periodType: v as GoalPeriodType })}
                      options={periodOptions}
                    />
                  </motion.div>

                  {/* Custom period dates */}
                  {form.periodType === "custom" && (
                    <motion.div
                      className="grid grid-cols-2 gap-3"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: nextDelay() }}
                    >
                      <div className="space-y-2">
                        <FieldLabel
                          htmlFor="goal-start"
                          hint="The day this custom period begins measuring progress."
                        >
                          Start date
                        </FieldLabel>
                        <Input
                          id="goal-start"
                          type="date"
                          className="rounded-lg"
                          value={form.periodStart}
                          onChange={(e) => patch({ periodStart: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <FieldLabel
                          htmlFor="goal-end"
                          hint="The day this custom period ends and the goal stops measuring progress."
                        >
                          End date
                        </FieldLabel>
                        <Input
                          id="goal-end"
                          type="date"
                          className="rounded-lg"
                          value={form.periodEnd}
                          onChange={(e) => patch({ periodEnd: e.target.value })}
                        />
                      </div>
                      {errors.periodStart && (
                        <p className="col-span-2 text-[12px] text-destructive">
                          {errors.periodStart}
                        </p>
                      )}
                    </motion.div>
                  )}

                  {/* Budget inverted-pace helper copy */}
                  {isBudget && (
                    <motion.p
                      className="text-[12px] text-muted-foreground"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: nextDelay() }}
                    >
                      On track = projected spend within budget.
                    </motion.p>
                  )}

                  {/* Thresholds */}
                  <motion.div
                    className="grid grid-cols-2 gap-3"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: nextDelay() }}
                  >
                    <div className="space-y-2">
                      <FieldLabel
                        htmlFor="goal-warn"
                        hint="When progress reaches this share of the target, the goal raises an early warning."
                      >
                        Warning %
                      </FieldLabel>
                      <Input
                        id="goal-warn"
                        type="number"
                        className="rounded-lg"
                        value={form.thresholdWarning}
                        onChange={(e) => patch({ thresholdWarning: e.target.value })}
                      />
                      {errors.thresholdWarning && (
                        <p className="text-[12px] text-destructive">
                          {errors.thresholdWarning}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <FieldLabel
                        htmlFor="goal-danger"
                        hint="When progress reaches this share of the target, the goal flags as critical."
                      >
                        Danger %
                      </FieldLabel>
                      <Input
                        id="goal-danger"
                        type="number"
                        className="rounded-lg"
                        value={form.thresholdDanger}
                        onChange={(e) => patch({ thresholdDanger: e.target.value })}
                      />
                    </div>
                  </motion.div>

                  {/* The 403 UpgradeRequiredError surfaces as the modal below,
                      NOT this inline toast (AC#9) — suppress it here. */}
                  {mutation.isError && !isUpgradeProblem(mutation.error) && (
                    <p className="text-[12px] text-destructive" role="alert">
                      {(mutation.error as { detail?: string; title?: string })?.detail ??
                        (mutation.error as { title?: string })?.title ??
                        "Failed to save goal"}
                    </p>
                  )}

                  <div className="flex justify-end gap-3 mt-6">
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-2xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                      style={{ border: "0.5px solid var(--hairline)" }}
                      onClick={() => handleOpenChange(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="brand"
                      className="rounded-2xl text-[13px] cursor-pointer"
                      disabled={mutation.isPending}
                    >
                      {mutation.isPending
                        ? "Saving…"
                        : isEdit
                          ? "Save changes"
                          : "Create goal"}
                    </Button>
                  </div>
                </form>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>

    {/* Story 18.10 — per-client / 403 upgrade prompt (AC#8/#9). Always
        mounted so its own AnimatePresence can play the close animation. */}
    <UpgradeLimitModal
      open={gateModal !== null}
      onOpenChange={(o) => !o && setGateModal(null)}
      feature="goalLimit"
      currentPlan={gateModal?.currentPlan ?? plan}
      currentUsage={gateModal?.currentUsage ?? 0}
      currentLimit={gateModal?.currentLimit ?? 0}
      upgradePlan={gateModal?.upgradePlan}
      nextTierLimit={gateModal?.nextTierLimit ?? null}
      billingInterval="monthly"
    />
    </>
  );
}
